import React, { useCallback, useRef, useState } from 'react';
import { ComponentWrapper } from '../../../helperComponents/componentWrapper/ComponentWrapper';
import sharedStyles from '../Shared/styles';
import {
    Platform,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
    Linking,
    ActivityIndicator,
} from 'react-native';
import { Header } from '../Shared/Header';
import { GradientButton } from '../../../helperComponents/gradients/GradientButton';
import PeopleIcon from '../../../../resources/svgComponents/PeopleIcon';
import {
    mbApplyTransparency,
    mbTextStyles,
} from '@mightybyte/rnw.utils.style-utils';
import { textStyles } from '../../../../constants/textStyles';
import { SapphicsView } from './components/SapphicsView';
import {
    ShadowTextInput,
    TextInputProps,
} from '../../../helperComponents/ShadowTextInput';
import Ionicons from 'react-native-vector-icons/Ionicons';
import Fontisto from 'react-native-vector-icons/Fontisto';
import { ShadowView } from '../../../helperComponents/ShadowView';
import FilterIcon from '../../../../resources/svgComponents/FilterIcon';
import { Spacer } from '../../../helperComponents/Spacer';
import FilterModal, { Filter } from './modals/FilterModal';
import Search from './components/Search';
import XIcon from '../../../../resources/svgComponents/XIcon';
import SapphicProfileModal from './modals/SapphicProfileModal';
import { useModalState } from '../../../../utils/shared';
import { MB_TextInputRef } from '@mightybyte/rnw.components.text-input';
import { useFocusEffect } from '@react-navigation/core';
import { setAdjustResize, setAdjustPan } from 'rn-android-keyboard-adjust';
// import { NativeStackNavigationProp } from '@react-navigation/native-stack';
// import { RootStackParamList } from '../../../../navigations/RootNavigator';
import { useTracker } from '../../../../hooks/useTracker';
import { UserVisit } from '@sapphicsavvy/business';
import { envs } from '../../../../../env';
import { trpc } from '../../../../apiCalls/trpcClient';


function useFilter() {
    const [isVisible, setIsVisible] = useState(false);
    const [filter, setFilter] = useState<Filter>();
    const [query, setQuery] = useState<string>();

    return {
        query,
        setQuery,
        filter,
        isVisible,
        show: useCallback(() => setIsVisible(true), []),
        hide: useCallback((updatedFilter?: Filter) => {
            setFilter((prev) => updatedFilter ?? prev);
            setIsVisible(false);
        }, []),
    };
}

function SearchInput(props: TextInputProps) {
    return (
        <View style={styles.textInputContainer}>
            <ShadowTextInput
                {...props}
                placeholder="Search by name"
                containerStyle={styles.textInputContainer}
                style={styles.textInput}
                textInputStyle={styles.textInputText}
                leftIcon={<Ionicons name="search" size={24} color="#F35E2E" />}
                keyboardType="default"
            />
            <TouchableOpacity
                style={[styles.clearInput, !props.value && styles.hide]}
                onPress={() => props.onChangeText?.('')}
            >
                <XIcon size={12} color="#F35E2E" />
            </TouchableOpacity>
        </View>
    );
}

export function Sapphics() {
    const {  track } = useTracker();

    const canReportFocusState = useRef(true);
    const textRef = useRef<MB_TextInputRef>(null);
    const [textFocus, setTextFocus] = useState(false);
    const [isLoadingForum, setIsLoadingForum] = useState(false);

    const {
        filter,
        isVisible,
        show: showFilter,
        hide: hideFilter,
        query,
        setQuery,
    } = useFilter();
    const {
        state: userId,
        show: showUser,
        hide: hideUser,
    } = useModalState<string>();

    const numOfFilter = Object.entries(filter ?? {}).filter(
        ([_, value]) => value !== undefined,
    ).length;

    const getForumSSOUrl = trpc.user.getForumSSOUrl.useQuery({});

    // Check if app features are unlocked (user has played quarter)
    const { data: unlockedObj } = trpc.quarter.appUnlocked.useQuery();
    const isLocked = unlockedObj?.isUnlocked !== undefined ? !unlockedObj.isUnlocked : false;

    useFocusEffect(
        useCallback(() => {
            setAdjustPan();
            return () => setAdjustResize();
        }, []),
    );

    useFocusEffect(
        useCallback(() => {
            track(UserVisit.sapphics);
        }, [track]),
    );

    const onModalHide = useCallback(() => {
        if (Platform.OS === 'android' && textFocus) {
            textRef.current?.blur();
            canReportFocusState.current = false;
            setTimeout(() => {
                textRef.current?.focus();
                canReportFocusState.current = true;
            }, 200);
        }
    }, [textFocus]);

    const handleOpenForum = useCallback(async () => {
        try {
            setIsLoadingForum(true);
            track(UserVisit.socialize);

            // Fetch forum SSO URL
            const result = await getForumSSOUrl.refetch();

            if (!result.data?.url) {
                console.error('No forum URL returned');
                return;
            }

            // Open forum URL
            const supported = await Linking.canOpenURL(result.data.url);
            if (supported) {
                await Linking.openURL(result.data.url);
            } else {
                console.error('Cannot open forum URL:', result.data.url);
            }
        } catch (error) {
            console.error('Error opening forum:', error);
        } finally {
            setIsLoadingForum(false);
        }
    }, [getForumSSOUrl, track]);

    return (
        <ComponentWrapper
            containerStyle={[sharedStyles.container]}
            wrapInScrollView={false}
        >
            <Header
                title="SAPPHICS"
                rightElements={
                    envs.IS_CHAT_ENABLED ? (
                        <View style={{position: 'relative'}}>
                            <GradientButton
                                title="Social Feed"
                                leftElement={isLocked ? <Fontisto name={'locked'} size={18} color={'#fff'}/>: <PeopleIcon color="#FFF" />}
                                style={styles.socialize}
                                colors={['#F35E2E', '#F35E2E']}
                                onPress={handleOpenForum}
                                disabled={isLoadingForum || isLocked}
                            />
                            {isLocked ? <Fontisto name={'locked'} size={20} style={{
                                position:'absolute',
                                top: 7,
                                left: 16,
                            }} color={'#fff'} /> : null}
                        </View>

                    ) : (
                        <View />
                    )
                }
            />
            <View style={styles.filter}>
                <SearchInput
                    value={query}
                    onChangeText={setQuery}
                    MB_Ref={textRef}
                    onFocus={() =>
                        canReportFocusState.current && setTextFocus(true)
                    }
                    onBlur={() =>
                        canReportFocusState.current && setTextFocus(false)
                    }
                />
                <Spacer width={10} />
                <ShadowView style={styles.filterBtn} onPress={showFilter}>
                    <FilterIcon />
                    {numOfFilter > 0 && (
                        <View style={styles.filterNote}>
                            <Text style={styles.filterNoteText}>
                                {numOfFilter}
                            </Text>
                        </View>
                    )}
                </ShadowView>
                <Search
                    query={query}
                    style={[styles.search, !textFocus && styles.hide]}
                    onViewUser={showUser}
                />
            </View>
            <View style={styles.sapphics}>
                <SapphicsView
                    filter={filter}
                    query={query}
                    onViewUser={showUser}
                />
                <View style={[styles.backdrop, !textFocus && styles.hide]} />
            </View>
            <SapphicProfileModal
                userId={userId}
                onDismiss={hideUser}
                onModalHide={onModalHide}
                isLocked={isLocked}
            />
            <FilterModal
                isVisible={isVisible}
                filter={filter}
                onDismiss={hideFilter}
                onModalHide={onModalHide}
            />
            {isLoadingForum && (
                <View style={styles.loaderOverlay}>
                    <View style={styles.loaderContainer}>
                        <ActivityIndicator size="large" color="#F35E2E" />
                        <Text style={styles.loaderText}>
                            Opening forum...
                        </Text>
                    </View>
                </View>
            )}
        </ComponentWrapper>
    );
}

const styles = StyleSheet.create({
    socialize: {
        height: 38,
        borderColor: mbApplyTransparency('#FFFFFF', 0.5),
        borderWidth: 1,
        borderRadius: 10,
    },
    filter: {
        flexDirection: 'row',
        marginBottom: 20,
    },
    textInputContainer: {
        flex: 1,
    },
    textInput: {
        paddingHorizontal: 15,
    },
    textInputText: mbTextStyles([
        textStyles.smallText,
        {
            fontWeight: '400',
            color: '#000000',
            marginLeft: 6,
            textAlign: 'left',
        },
    ]),
    clearInput: {
        position: 'absolute',
        width: 25,
        height: 25,
        right: 15,
        top: 12,
        backgroundColor: '#FFFFFF',
        borderRadius: 7,
        alignItems: 'center',
        justifyContent: 'center',
    },
    filterBtn: {
        height: 50,
        width: 50,
        backgroundColor: '#EFE7E8',
        borderColor: mbApplyTransparency('#FFFFFF', 0.55),
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
    },
    filterNote: {
        borderRadius: 360,
        backgroundColor: '#E50914',
        position: 'absolute',
        height: 15,
        width: 15,
        right: -5,
        top: -5,
        alignItems: 'center',
        justifyContent: 'center',
    },
    filterNoteText: mbTextStyles(textStyles.tinyText),
    search: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 58,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: mbApplyTransparency('#D1D1D1', 0.55),
    },
    sapphics: {
        flex: 1,
        marginHorizontal: -16,
        zIndex: -1,
    },
    tab: {
        display: 'none',
    },
    absolute: {
        position: 'absolute',
        width: '100%',
        height: 47,
    },
    tabs: {
        height: 47,
        flexDirection: 'row',
        paddingHorizontal: 10,
        backgroundColor: '#f0f',
    },
    tabsBtn: {
        height: 41,
        marginTop: 6,
        flexGrow: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    tabsBtnActive: {
        borderTopRightRadius: 5,
        borderTopLeftRadius: 5,
        backgroundColor: '#FFFFFF',
    },
    tabsBtnText: mbTextStyles([
        textStyles.normalText,
        {
            textAlign: 'left',
            color: '#FFFFFF',
            fontWeight: '700',
        },
    ]),
    tabsBtnTextActive: {
        color: '#F35E2E',
    },
    backdrop: {
        position: 'absolute',
        backgroundColor: mbApplyTransparency('#000000', 0.47),
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
    },
    hide: {
        display: 'none',
    },
    loaderOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: mbApplyTransparency('#000000', 0.7),
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
    },
    loaderContainer: {
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        padding: 32,
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 200,
    },
    loaderText: mbTextStyles([
        textStyles.normalText,
        {
            color: '#F35E2E',
            fontWeight: '600',
            marginTop: 16,
        },
    ]),
});
